import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  FileText, Upload, Download, Eye, DollarSign, CheckCircle, 
  Clock, AlertTriangle, Building2, TrendingUp, FileCheck, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientSpace = ({ user }) => {
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Upload state
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadType, setUploadType] = useState('kbis');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  // Quote request state
  const [quoteRequest, setQuoteRequest] = useState({
    service_type: 'Installation complète',
    description: '',
    urgency: 'medium',
    budget_range: ''
  });

  useEffect(() => {
    if (user?.client_id) {
      loadClientData();
    }
  }, [user]);

  const loadClientData = async () => {
    setLoading(true);
    try {
      // Load profile
      const profileRes = await axios.get(`${API}/clients/${user.client_id}/profile`);
      setProfile(profileRes.data);
      setDocuments(profileRes.data.documents || []);
      
      // Load quotes
      const quotesRes = await axios.get(`${API}/quotes/list`);
      setQuotes(quotesRes.data || []);
    } catch (error) {
      console.error('Error loading client data:', error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    setUploadFiles(prev => [...prev, ...fileArray]);
  };

  const removeFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadDocuments = async () => {
    if (uploadFiles.length === 0) {
      toast.error("Aucun fichier sélectionné");
      return;
    }

    setUploading(true);
    try {
      for (const file of uploadFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', uploadType);

        await axios.post(`${API}/clients/${user.client_id}/upload-document`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success(`${uploadFiles.length} document(s) téléchargé(s) avec succès !`);
      setUploadFiles([]);
      loadClientData();
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const requestQuote = async () => {
    if (!quoteRequest.description) {
      toast.error("Veuillez décrire votre demande");
      return;
    }

    try {
      await axios.post(`${API}/quotes/request`, {
        client_id: user.client_id,
        ...quoteRequest
      });

      toast.success("Demande de devis envoyée !");
      setQuoteRequest({
        service_type: 'Installation complète',
        description: '',
        urgency: 'medium',
        budget_range: ''
      });
      loadClientData();
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
      console.error(error);
    }
  };

  const acceptQuote = async (quoteId) => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/accept`);
      toast.success("Devis accepté !");
      loadClientData();
    } catch (error) {
      toast.error("Erreur lors de l'acceptation");
      console.error(error);
    }
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      kbis: 'KBIS',
      rib: 'RIB',
      tva: 'N° TVA',
      siret: 'SIRET',
      insurance: 'Assurance',
      impact_study: 'Étude d\'impact',
      floor_plan: 'Plan',
      sound_system: 'Système sono',
      other_admin: 'Autre'
    };
    return labels[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      active: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getQuoteStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      sent: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Calculate required documents count
  const requiredDocs = ['kbis', 'rib', 'siret'];
  const uploadedRequiredDocs = documents.filter(doc => 
    requiredDocs.includes(doc.document_type)
  ).length;
  const docsProgress = (uploadedRequiredDocs / requiredDocs.length) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement de votre espace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {profile?.company_name}
              </h1>
              <p className="text-slate-600 mt-1">Espace Client T-Kalé Pro-Acoustics</p>
            </div>
            <div className="flex items-center space-x-3">
              <Badge className={getStatusColor(profile?.status)}>
                {profile?.status}
              </Badge>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  localStorage.removeItem('token');
                  window.location.href = '/login';
                }}
              >
                Déconnexion
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Documents</p>
                  <p className="text-2xl font-bold">{documents.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <FileCheck className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Docs obligatoires</p>
                  <p className="text-2xl font-bold">{uploadedRequiredDocs}/3</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-purple-100 rounded-full">
                  <DollarSign className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Devis</p>
                  <p className="text-2xl font-bold">{quotes.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-teal-100 rounded-full">
                  <TrendingUp className="h-6 w-6 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Statut</p>
                  <p className="text-sm font-bold">{profile?.status === 'active' ? 'Actif' : 'En cours'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Alert */}
        {profile?.status === 'pending' && (
          <Alert className="bg-yellow-50 border-yellow-200">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription>
              <strong>Compte en attente de validation</strong>
              <p className="mt-1 text-sm">Votre inscription est en cours de vérification par notre équipe. Vous recevrez un email dès la validation (24-48h).</p>
            </AlertDescription>
          </Alert>
        )}

        {docsProgress < 100 && profile?.status !== 'pending' && (
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <div>
                  <strong>Documents obligatoires incomplets</strong>
                  <p className="mt-1 text-sm">Veuillez télécharger : KBIS, RIB et SIRET pour activer pleinement votre compte.</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">{Math.round(docsProgress)}%</div>
                  <div className="text-xs text-slate-500">Complété</div>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Main Content */}
        <Tabs defaultValue="documents" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="documents">📄 Documents</TabsTrigger>
            <TabsTrigger value="quotes">💰 Devis</TabsTrigger>
            <TabsTrigger value="profile">👤 Mon Profil</TabsTrigger>
          </TabsList>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents" className="space-y-6">
            {/* Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Upload className="h-5 w-5" />
                  <span>Télécharger des documents</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Type de document</Label>
                  <Select value={uploadType} onValueChange={setUploadType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kbis">📋 KBIS (obligatoire)</SelectItem>
                      <SelectItem value="rib">💳 RIB (obligatoire)</SelectItem>
                      <SelectItem value="siret">🏢 SIRET (obligatoire)</SelectItem>
                      <SelectItem value="tva">📊 N° TVA</SelectItem>
                      <SelectItem value="insurance">🛡️ Assurance</SelectItem>
                      <SelectItem value="impact_study">📈 Étude d'impact acoustique</SelectItem>
                      <SelectItem value="floor_plan">🗺️ Plan de l'établissement</SelectItem>
                      <SelectItem value="sound_system">🔊 Système de sonorisation</SelectItem>
                      <SelectItem value="other_admin">📎 Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Drag & Drop Zone */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive ? 'border-teal-500 bg-teal-50' : 'border-slate-300 bg-slate-50'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <Upload className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                  <p className="text-slate-600 mb-2">
                    Glissez-déposez vos fichiers ici ou
                  </p>
                  <Input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-input"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('file-input').click()}
                  >
                    Parcourir les fichiers
                  </Button>
                  <p className="text-xs text-slate-500 mt-2">
                    PDF, DOC, DOCX, JPG, PNG (max 10 MB par fichier)
                  </p>
                </div>

                {/* Files List */}
                {uploadFiles.length > 0 && (
                  <div className="space-y-2">
                    <Label>Fichiers sélectionnés ({uploadFiles.length})</Label>
                    {uploadFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white rounded border">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-4 w-4 text-teal-600" />
                          <span className="text-sm">{file.name}</span>
                          <span className="text-xs text-slate-500">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                    
                    <Button
                      onClick={uploadDocuments}
                      disabled={uploading}
                      className="w-full bg-teal-600 hover:bg-teal-700"
                    >
                      {uploading ? 'Téléchargement...' : `Télécharger ${uploadFiles.length} fichier(s)`}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Documents List */}
            <Card>
              <CardHeader>
                <CardTitle>Mes documents ({documents.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>Aucun document téléchargé</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <div key={doc.document_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-center space-x-3">
                          <FileText className="h-5 w-5 text-teal-600" />
                          <div>
                            <p className="font-medium">{getDocumentTypeLabel(doc.document_type)}</p>
                            <p className="text-sm text-slate-500">{doc.file_name}</p>
                            <p className="text-xs text-slate-400">
                              Téléchargé le {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          {doc.is_validated && (
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Validé
                            </Badge>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* QUOTES TAB */}
          <TabsContent value="quotes" className="space-y-6">
            {/* Request Quote Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <DollarSign className="h-5 w-5" />
                  <span>Demander un devis</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Type de service</Label>
                  <Select 
                    value={quoteRequest.service_type} 
                    onValueChange={(val) => setQuoteRequest({...quoteRequest, service_type: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Installation complète">Installation complète</SelectItem>
                      <SelectItem value="Maintenance">Maintenance</SelectItem>
                      <SelectItem value="Étude acoustique">Étude acoustique</SelectItem>
                      <SelectItem value="Mise en conformité">Mise en conformité</SelectItem>
                      <SelectItem value="Audit">Audit acoustique</SelectItem>
                      <SelectItem value="Autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Description de votre besoin *</Label>
                  <Textarea
                    value={quoteRequest.description}
                    onChange={(e) => setQuoteRequest({...quoteRequest, description: e.target.value})}
                    placeholder="Décrivez précisément votre besoin..."
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Urgence</Label>
                    <Select 
                      value={quoteRequest.urgency} 
                      onValueChange={(val) => setQuoteRequest({...quoteRequest, urgency: val})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Faible</SelectItem>
                        <SelectItem value="medium">Moyenne</SelectItem>
                        <SelectItem value="high">Élevée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Budget indicatif (optionnel)</Label>
                    <Input
                      value={quoteRequest.budget_range}
                      onChange={(e) => setQuoteRequest({...quoteRequest, budget_range: e.target.value})}
                      placeholder="Ex: 3000-5000 €"
                    />
                  </div>
                </div>

                <Button onClick={requestQuote} className="w-full bg-teal-600 hover:bg-teal-700">
                  Envoyer la demande
                </Button>
              </CardContent>
            </Card>

            {/* Quotes List */}
            <Card>
              <CardHeader>
                <CardTitle>Mes devis ({quotes.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {quotes.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>Aucune demande de devis</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {quotes.map((quote) => (
                      <div key={quote.quote_id} className="p-4 bg-slate-50 rounded-lg border">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-medium">{quote.service_type}</h4>
                            <Badge className={getQuoteStatusColor(quote.status)} size="sm">
                              {quote.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                              {quote.status === 'sent' && <AlertCircle className="h-3 w-3 mr-1" />}
                              {quote.status === 'accepted' && <CheckCircle className="h-3 w-3 mr-1" />}
                              {quote.status}
                            </Badge>
                          </div>
                          <span className="text-xs text-slate-500">
                            {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>

                        <p className="text-sm text-slate-600 mb-3">{quote.description}</p>

                        {quote.quote_amount && (
                          <div className="p-3 bg-green-50 rounded border border-green-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-slate-600">Montant du devis</p>
                                <p className="text-2xl font-bold text-green-700">{quote.quote_amount} €</p>
                                {quote.quote_valid_until && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    Valable jusqu'au {new Date(quote.quote_valid_until).toLocaleDateString('fr-FR')}
                                  </p>
                                )}
                              </div>
                              {quote.status === 'sent' && (
                                <div className="flex space-x-2">
                                  <Button 
                                    size="sm" 
                                    onClick={() => acceptQuote(quote.quote_id)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    Accepter
                                  </Button>
                                  <Button size="sm" variant="outline">
                                    Refuser
                                  </Button>
                                </div>
                              )}
                            </div>
                            {quote.quote_notes && (
                              <p className="text-sm text-slate-600 mt-2 pt-2 border-t border-green-200">
                                {quote.quote_notes}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PROFILE TAB */}
          <TabsContent value="profile" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Informations de l'établissement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Nom de l'établissement</Label>
                    <p className="font-medium">{profile?.company_name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Type</Label>
                    <p className="font-medium">{profile?.establishment_type}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">SIRET</Label>
                    <p className="font-medium">{profile?.siret || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">N° TVA</Label>
                    <p className="font-medium">{profile?.tva_number || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Capacité d'accueil</Label>
                    <p className="font-medium">{profile?.establishment_capacity || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Système sono</Label>
                    <p className="font-medium">{profile?.sound_system_info || 'Non renseigné'}</p>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Label className="text-slate-500">Adresse</Label>
                  <p className="font-medium">
                    {profile?.address}<br />
                    {profile?.postal_code} {profile?.city}
                  </p>
                </div>

                <div className="pt-4 border-t">
                  <Label className="text-slate-500">Contact</Label>
                  <p className="font-medium">{profile?.contact_person}</p>
                  <p className="text-slate-600">{profile?.email}</p>
                  <p className="text-slate-600">{profile?.phone}</p>
                </div>

                <div className="pt-4 border-t">
                  <Label className="text-slate-500">Problématique</Label>
                  <p className="text-slate-600 whitespace-pre-wrap">{profile?.problem_description}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ClientSpace;
